/**
 * "Import old project files" on Insights (R249, decision 1049; help `cloud.insights`; docs/specs/cloud/insights.md
 * §8). Teslyn reads the chosen `.tjf` files in the browser, as "Import aircraft" does: the file itself never leaves the
 * computer, only the production figures of the jobs ticked here. A job already in Teslyn cannot be ticked, and is never
 * overwritten.
 */
import { useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  TjrFileError,
  planHistoryImport,
  readTjrHistoryJob,
  type HistoryImportRow,
  type HistoryJob,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { TableWrap } from '../components/TableWrap.tsx';
import { TickCell } from '../components/TickCell.tsx';
import { fileBytes } from '../components/fileBytes.ts';
import { dateText, daysText, hoursText } from '../ops/format.ts';
import { addHistoryJob } from './api.ts';
import { kmOf } from './format.ts';

export interface HistoryImportPanelProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  /** Live projects and history jobs as this page read them; the database checks again on import. */
  readonly projectNumbers: readonly string[];
  readonly historyNumbers: readonly string[];
  readonly onDone: (message: string) => void;
  readonly onCancel: () => void;
}

const datesText = (job: HistoryJob): string =>
  job.first_date && job.last_date ? `${dateText(job.first_date)} to ${dateText(job.last_date)}` : '–';

export function HistoryImportPanel({
  sb,
  tenantId,
  projectNumbers,
  historyNumbers,
  onDone,
  onCancel,
}: HistoryImportPanelProps): ReactElement {
  const [rows, setRows] = useState<HistoryImportRow[] | null>(null);
  const [unread, setUnread] = useState<readonly string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function choose(chosen: readonly File[]): Promise<void> {
    if (chosen.length === 0) return;
    setError(null);
    setRows(null);
    setUnread([]);
    setBusy(true);
    const jobs: HistoryJob[] = [];
    const bad: string[] = [];
    for (const f of chosen) {
      try {
        jobs.push(readTjrHistoryJob(await fileBytes(f), f.name));
      } catch (e) {
        // one bad file does not stop the others
        bad.push(`${f.name}: ${e instanceof TjrFileError ? e.message : 'the file could not be read.'}`);
      }
    }
    setUnread(bad);
    setRows(planHistoryImport(jobs, projectNumbers, historyNumbers));
    setBusy(false);
  }

  const ticked = rows?.filter((r) => r.selected && r.status === 'new') ?? [];
  const toggle = (row: HistoryImportRow, on: boolean): void =>
    setRows((list) => (list ?? []).map((r) => (r === row ? { ...r, selected: on } : r)));

  async function apply(): Promise<void> {
    setBusy(true);
    setError(null);
    let added = 0;
    try {
      for (const r of ticked) {
        await addHistoryJob(sb, tenantId, r.job);
        added += 1;
      }
      onDone(`Imported ${added} ${added === 1 ? 'job' : 'jobs'} as history.`);
    } catch (e) {
      const why = e instanceof Error ? e.message : String(e);
      if (added > 0) onDone(`Imported ${added} of ${ticked.length} jobs. The next one failed: ${why}`);
      else setError(why);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel stack" aria-label="Import old project files">
      <h2 className="heading">Import old project files</h2>
      <p className="body">
        Choose one or more job files (<span className="data">Report_Log_*.tjf</span>). Teslyn reads them on
        this computer and keeps only each job&apos;s dates, hours, fuel litres, standby days, pilots, aircraft
        and line km, as history for this page. No prices come across. History jobs show on Insights only: they
        never appear as projects, on the Operations Hub, to clients, or in billing or licence counts. A job
        already in Teslyn is never overwritten.
      </p>
      <label className="field">
        <span className="label">Job files (Report_Log_*.tjf)</span>
        <input
          type="file"
          multiple
          accept=".tjf"
          onChange={(e) => {
            const chosen = Array.from(e.target.files ?? []);
            e.target.value = '';
            void choose(chosen);
          }}
        />
      </label>
      {error && (
        <p className="notice notice--fail" role="alert">
          {error}
        </p>
      )}
      {unread.length > 0 && (
        <div className="notice notice--fail" role="alert">
          <ul aria-label="Files that could not be read">
            {unread.map((u) => (
              <li key={u}>{u}</li>
            ))}
          </ul>
        </div>
      )}
      {rows && rows.length > 0 && (
        <TableWrap>
          <table className="table" aria-label="Jobs to import">
            <thead>
              <tr>
                <th className="label">
                  <span className="sr-only">Import</span>
                </th>
                <th className="label">Job</th>
                <th className="label">Dates</th>
                <th className="label num">Km flown</th>
                <th className="label num">Accepted km</th>
                <th className="label num">Flight hours</th>
                <th className="label num">Survey hours</th>
                <th className="label num">Standby</th>
                <th className="label">Pilots</th>
                <th className="label">What happens</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.job.file_name}|${r.job.number}`}>
                  <td>
                    {r.status === 'new' && (
                      <TickCell
                        aria-label={`Import ${r.job.number}`}
                        checked={r.selected}
                        onChange={(e) => toggle(r, e.target.checked)}
                      />
                    )}
                  </td>
                  <td>
                    <span className="data">{r.job.number}</span> {r.job.title}
                    <span className="caption muted"> {r.job.file_name}</span>
                  </td>
                  <td>{datesText(r.job)}</td>
                  <td className="num data">{kmOf(r.preview.flownKm)}</td>
                  <td className="num data">{kmOf(r.preview.acceptedKm)}</td>
                  <td className="num data">{hoursText(r.preview.flightHours)}</td>
                  <td className="num data">{hoursText(r.preview.surveyHours)}</td>
                  <td className="num data">{daysText(r.preview.standbyDays)}</td>
                  <td>{r.preview.pilots.join(', ') || '–'}</td>
                  <td>{r.status === 'new' ? 'Add as history' : r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      <div className="row">
        <Button variant="primary" busy={busy} disabled={ticked.length === 0} onClick={() => void apply()}>
          {`Import ${ticked.length} ${ticked.length === 1 ? 'job' : 'jobs'}`}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}
